/**
 * Mobile Viewport Utilities
 * Handles mobile viewport height issues, especially with virtual keyboards
 */

import { logDebug, logInfo } from '@/utils/secureLogger'

/**
 * Sets up mobile viewport height handling for virtual keyboard compatibility
 * This is a fallback for browsers that don't support dvh (dynamic viewport height)
 */
export function setupMobileViewport(): void {
  // Only run on mobile devices
  if (!isMobileDevice()) {
    return
  }

  logDebug('Setting up mobile viewport height handling')

  // Set initial viewport height
  updateViewportHeight()

  // Update on resize (when keyboard shows/hides)
  window.addEventListener('resize', updateViewportHeight)
  window.addEventListener('orientationchange', () => {
    // Delay to allow orientation change to complete
    setTimeout(updateViewportHeight, 100)
  })

  // Handle visual viewport API if available (better keyboard detection)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateViewportHeight)
  }

  logInfo('Mobile viewport height handling initialized')
}

/**
 * Updates the CSS custom property for viewport height
 */
function updateViewportHeight(): void {
  // Get the actual viewport height
  const vh = window.innerHeight * 0.01
  
  // Set the CSS custom property
  document.documentElement.style.setProperty('--vh', `${vh}px`)
  
  // Also update the mobile-vh property
  document.documentElement.style.setProperty('--mobile-vh', `${window.innerHeight}px`)
  
  logDebug('Updated viewport height', { 
    innerHeight: window.innerHeight, 
    vh: vh,
    visualViewportHeight: window.visualViewport?.height 
  })
}

/**
 * Detects if the current device is likely a mobile device
 */
function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         window.innerWidth <= 1024
}

/**
 * Checks if the virtual keyboard is currently visible
 * This is a heuristic based on viewport height changes
 */
export function isVirtualKeyboardVisible(): boolean {
  if (!window.visualViewport) {
    // Fallback: compare current height to initial height
    const currentHeight = window.innerHeight
    const initialHeight = window.screen.height
    
    // If current height is significantly less than screen height, keyboard is likely visible
    return currentHeight < initialHeight * 0.75
  }
  
  // Use Visual Viewport API for more accurate detection
  return window.visualViewport.height < window.innerHeight * 0.75
}

/**
 * Scrolls an element into view, accounting for virtual keyboard
 */
export function scrollIntoViewSafe(element: HTMLElement, options?: ScrollIntoViewOptions): void {
  if (!element) return
  
  const defaultOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest'
  }
  
  const scrollOptions = { ...defaultOptions, ...options }
  
  // If keyboard is visible, adjust the scroll behavior
  if (isVirtualKeyboardVisible()) {
    // Scroll with some extra padding to ensure visibility above keyboard
    element.scrollIntoView(scrollOptions)
    
    // Additional adjustment for mobile keyboards
    setTimeout(() => {
      const rect = element.getBoundingClientRect()
      const keyboardHeight = window.innerHeight - (window.visualViewport?.height || window.innerHeight)
      
      if (rect.bottom > window.innerHeight - keyboardHeight - 20) {
        window.scrollBy(0, rect.bottom - (window.innerHeight - keyboardHeight - 20))
      }
    }, 100)
  } else {
    element.scrollIntoView(scrollOptions)
  }
}

/**
 * Cleanup function to remove event listeners
 */
export function cleanupMobileViewport(): void {
  window.removeEventListener('resize', updateViewportHeight)
  window.removeEventListener('orientationchange', updateViewportHeight)
  
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', updateViewportHeight)
  }
  
  logDebug('Mobile viewport height handling cleaned up')
}
